import React, { useMemo, useState } from 'react';
import { message } from 'antd';
import { PencilSimple } from '@phosphor-icons/react';
import { useDashboardV3Data } from './useDashboardV3Data';
import { DashboardV3ProductCard } from './DashboardV3ProductCard';
import { useOneButtonStore } from '../../../../../../store/oneButtonStore';
import { isHoverRoofDesignEnabled } from '../../../../../../services/oneButtonServices/hoverRoofDesignGate';
import { applyHoverEstimateConvert } from '../../../../../../services/oneButtonServices/applyHoverEstimateConvert';
import type { HoverRoofEstimate } from '../../../../../../services/oneButtonServices/hoverServices';
import HoverRoofEstimatesModal from '../../HoverRoofEstimatesModal';
import HoverLogoMark from '../../HoverLogoMark';

export type DashboardV3SelectedProductsSectionProps = {
    onOpenProducts: () => void;
    /** Opens existing ProductDesignsModal / roof details when Hover is not gated. */
    onDesignRoof?: () => void;
    onDesignSolar?: () => void;
    onDesignBattery?: () => void;
    onUtility?: () => void;
    onAdders?: () => void;
};

export const DashboardV3SelectedProductsSection: React.FC<DashboardV3SelectedProductsSectionProps> = ({
    onOpenProducts,
    onDesignRoof,
    onDesignSolar,
    onDesignBattery,
    onUtility,
    onAdders,
}) => {
    const { products } = useDashboardV3Data();
    const setCommerceHubProductsOpen = useOneButtonStore((s) => s.setCommerceHubProductsOpen);
    const hoverJobId = useOneButtonStore((s) => s.hoverJobId);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const opportunityID = useOneButtonStore((s) => s.opportunityID);
    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const [hoverEstimatesOpen, setHoverEstimatesOpen] = useState(false);

    const hoverRoofDesignEnabled = useMemo(
        () =>
            isHoverRoofDesignEnabled({
                hoverJobId,
                opportunityDetails,
                onebuttonCapabilities,
            }),
        [hoverJobId, opportunityDetails, onebuttonCapabilities],
    );

    const openProducts = () => {
        setCommerceHubProductsOpen(true);
        onOpenProducts();
    };

    const openRoofDesign = () => {
        if (hoverRoofDesignEnabled) {
            setHoverEstimatesOpen(true);
            return;
        }
        if (onDesignRoof) {
            onDesignRoof();
            return;
        }
        openProducts();
    };

    const handleHoverConvert = (estimate: HoverRoofEstimate) => {
        applyHoverEstimateConvert(estimate);
        message.success('Hover roof estimate applied');
    };

    return (
        <section className="obd-dashboard-v3-layout__products" aria-label="Selected products">
            <div className="obd-v3-products-section-head">
                <h2 className="obd-dashboard-v3-layout__section-title">Selected products</h2>
                <button
                    type="button"
                    className="obd-v3-products-manage"
                    onClick={openProducts}
                >
                    <PencilSimple size={14} weight="regular" aria-hidden />
                    Manage products
                </button>
            </div>
            <div className="obd-v3-products-grid">
                {products.map((model) => (
                    <DashboardV3ProductCard
                        key={model.kind}
                        model={model}
                        designLabel={
                            model.kind === 'roof' && hoverRoofDesignEnabled ? (
                                <span className="obd-btn-with-hover-logo">
                                    <HoverLogoMark size="sm" />
                                    Roof Design
                                </span>
                            ) : (
                                'Design'
                            )
                        }
                        onDesign={
                            model.kind === 'roof'
                                ? openRoofDesign
                                : model.kind === 'solar'
                                  ? onDesignSolar || openProducts
                                  : model.kind === 'battery'
                                    ? onDesignBattery || openProducts
                                    : openProducts
                        }
                        onUtility={onUtility || openProducts}
                        onAdders={onAdders || openProducts}
                        onFinance={openProducts}
                    />
                ))}
            </div>
            <HoverRoofEstimatesModal
                open={hoverEstimatesOpen}
                onClose={() => setHoverEstimatesOpen(false)}
                opportunityId={opportunityID ?? opportunityDetails?.Opportunity_Id}
                onConvert={handleHoverConvert}
            />
        </section>
    );
};
